import dayjs from 'dayjs';

import { sortPeriods } from './periods';

import { StatementPeriod } from 'services/bookkeeping/queries/useStatementCoverage';

/** Abbreviated ("Jan") or full ("January") month name from a 1-based month number. */
function formatMonth(month: number, template: 'MMM' | 'MMMM'): string {
    return dayjs()
        .month(month - 1)
        .format(template);
}

/**
 * One month written out in full — "January 2026", for prose that names a single month where
 * the abbreviated `formatMonthList` form would read as a list of one.
 */
export function formatPeriod(period: StatementPeriod): string {
    return `${formatMonth(period.month, 'MMMM')} ${period.year}`;
}

/**
 * Abbreviated months grouped by year — "Jan, Feb, Mar 2026, Jun, Jul 2027". The year is stated
 * once per group, as the design writes it. Sorted defensively.
 */
export function formatMonthList(periods: StatementPeriod[]): string {
    if (periods.length === 0) {
        return '';
    }

    const groups: Array<{ year: number; months: number[] }> = [];

    sortPeriods(periods).forEach((period) => {
        const current = groups[groups.length - 1];
        if (current?.year === period.year) {
            current.months.push(period.month);
            return;
        }
        groups.push({ year: period.year, months: [period.month] });
    });

    return groups
        .map((group) => `${group.months.map((month) => formatMonth(month, 'MMM')).join(', ')} ${group.year}`)
        .join(', ');
}

/**
 * The span as first-month-to-last-month, in whichever register the caller reads in.
 *
 * One body because the interesting part is the three-way branch — a single month, a span inside one
 * year, a span crossing one — and stating that twice means finding a year-boundary bug twice.
 */
function formatRange(periods: StatementPeriod[], template: 'MMM' | 'MMMM', separator: string): string {
    if (periods.length === 0) {
        return '';
    }

    const sorted = sortPeriods(periods);
    const first = sorted[0];
    const last = sorted[sorted.length - 1];

    if (first.year === last.year && first.month === last.month) {
        return `${formatMonth(first.month, template)} ${first.year}`;
    }
    if (first.year === last.year) {
        return `${formatMonth(first.month, template)} ${separator} ${formatMonth(last.month, template)} ${last.year}`;
    }
    return (
        `${formatMonth(first.month, template)} ${first.year} ${separator} ` +
        `${formatMonth(last.month, template)} ${last.year}`
    );
}

/**
 * The requested span as prose — "January to July 2026", or "November 2025 to July 2026"
 * when it crosses a year. A single-month span collapses to "July 2026" rather than
 * repeating itself.
 */
export function formatMonthRange(periods: StatementPeriod[]): string {
    return formatRange(periods, 'MMMM', 'to');
}

/**
 * The same span abbreviated — "Jan – Jul 2026", or "Nov 2025 – Jul 2026" when it crosses a year.
 *
 * A separate exported name rather than a flag, because the two read in different places: the modal
 * names the ask in prose ("from January to July 2026"), while an account row states it as a label
 * beside a count, where the full month names wrap.
 */
export function formatMonthRangeShort(periods: StatementPeriod[]): string {
    return formatRange(periods, 'MMM', '–');
}

/**
 * A set of months as prose: one reads in full, several as the grouped abbreviated list.
 *
 * Shared because two surfaces make the same call — the row that names what a file covers and
 * the dialog that asks the member to confirm a month. Joining `formatPeriod` instead produced
 * "January 2026, February 2026", a month format used nowhere else.
 */
export function formatPeriods(periods: StatementPeriod[]): string {
    return periods.length === 1 ? formatPeriod(periods[0]) : formatMonthList(periods);
}
